import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, TextInput, ActivityIndicator, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Theme, useThemedStyles, useTheme } from '../theme';

interface Props {
  onBack: () => void;
  onSelectFeed?: (feed: any) => void;
  onFeedAdded?: () => void;
}

const FeedSearch: React.FC<Props> = ({ onBack, onSelectFeed, onFeedAdded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [feeds, setFeeds] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const searchFeeds = useCallback(async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setHasSearched(true);
    try {
      // Import dynamically to avoid circular dependencies
      const { searchFeeds: searchFeedsAPI } = await import('../services/bluesky');
      const results = await searchFeedsAPI(searchQuery);
      setFeeds(results);
    } catch (error) {
      console.error('Failed to search feeds:', error);
      Alert.alert(t('common.error'), t('feedSearch.searchError'));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, t]);

  const handleSaveFeed = useCallback(async (feed: any) => {
    try {
      const { saveFeed } = await import('../services/bluesky');
      await saveFeed(feed.uri);
      Alert.alert(t('common.info'), t('feedSearch.savedSuccess'));
      if (onFeedAdded) onFeedAdded();
    } catch (error) {
      console.error('Failed to save feed:', error);
      Alert.alert(t('common.error'), t('feedSearch.saveError'));
    }
  }, [t]);

  const renderFeedItem = ({ item }: { item: any }) => (
    <View style={styles.feedItem}>
      <TouchableOpacity 
        style={styles.feedContent}
        onPress={() => onSelectFeed?.(item)}
      >
        {item.avatar && (
          <Image source={{ uri: item.avatar }} style={styles.feedAvatar} cachePolicy="memory-disk" />
        )}
        <View style={styles.feedInfo}>
          <Text style={styles.feedName} numberOfLines={1}>{item.displayName}</Text>
          <Text style={styles.feedCreator} numberOfLines={1}>
            by @{item.creator?.handle || 'unknown'}
          </Text>
          {item.description && (
            <Text style={styles.feedDescription} numberOfLines={2}>{item.description}</Text>
          )}
          <Text style={styles.feedLikes}>
            <Ionicons name="heart" size={12} color={theme.colors.textMuted} /> {item.likeCount || 0}
          </Text>
        </View>
      </TouchableOpacity>
      <TouchableOpacity 
        style={styles.addButton}
        onPress={() => handleSaveFeed(item)}
      >
        <Ionicons name="add-circle" size={28} color={theme.colors.primary} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Ionicons name="chevron-back" size={28} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('feedSearch.title')}</Text>
        <View style={{ width: 28 }} />
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color={theme.colors.textMuted} />
          <TextInput
            style={styles.searchInput}
            placeholder={t('feedSearch.placeholder')}
            placeholderTextColor={theme.colors.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={searchFeeds}
            returnKeyType="search"
            autoFocus
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color={theme.colors.textMuted} />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity 
          style={[styles.searchButton, !searchQuery.trim() && styles.searchButtonDisabled]}
          onPress={searchFeeds}
          disabled={!searchQuery.trim() || loading}
        >
          <Text style={styles.searchButtonText}>{t('feedSearch.search')}</Text>
        </TouchableOpacity>
      </View>

      {/* Results */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : hasSearched ? (
        feeds.length > 0 ? (
          <FlatList
            data={feeds}
            keyExtractor={(item) => item.uri}
            renderItem={renderFeedItem}
            contentContainerStyle={styles.listContent}
          />
        ) : (
          <View style={styles.emptyContainer}>
            <Ionicons name="search-outline" size={48} color={theme.colors.textMuted} />
            <Text style={styles.emptyText}>{t('feedSearch.noResults')}</Text>
          </View>
        )
      ) : (
        <View style={styles.emptyContainer}>
          <Ionicons name="apps-outline" size={48} color={theme.colors.textMuted} />
          <Text style={styles.emptyText}>{t('feedSearch.searchHint')}</Text>
        </View>
      )}
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.card,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.input,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 16,
    color: theme.colors.text,
  },
  searchButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  searchButtonDisabled: {
    opacity: 0.5,
  },
  searchButtonText: {
    color: theme.colors.primaryText,
    fontWeight: '600',
    fontSize: 14,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textMuted,
    textAlign: 'center',
  },
  listContent: {
    paddingVertical: 8,
  },
  feedItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  feedContent: {
    flex: 1,
    flexDirection: 'row',
  },
  feedAvatar: {
    width: 50,
    height: 50,
    borderRadius: 8,
    backgroundColor: theme.colors.cardSecondary,
    marginRight: 12,
  },
  feedInfo: {
    flex: 1,
  },
  feedName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 2,
  },
  feedCreator: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginBottom: 4,
  },
  feedDescription: {
    fontSize: 13,
    color: theme.colors.text,
    marginBottom: 4,
  },
  feedLikes: {
    fontSize: 12,
    color: theme.colors.textMuted,
  },
  addButton: {
    padding: 8,
  },
});

export default FeedSearch;
